import { useMemo } from "react";
import { useLingui } from "@lingui/react/macro";
import type {
  HeaderNavigation,
  MobileMenuNavigation,
  NavLinkSection,
} from "@marketing-sites/ui/components";
import { EXTERNAL_ROUTES } from "@/constants/external-routes";
import IntercomLockup from "@/icons/intercom-lockup.svg";
import IntercomMark from "@/icons/logo-intercom.svg";

export function useHeaderNavigation() {
  const { t } = useLingui();

  return useMemo(() => {
    // ─────────────────────────────────────────────────────────────
    // Link data — declared once, referenced from both the desktop
    // `links` columns and the `mobileLinks` sections so there is a
    // single source of truth. Desktop/mobile grouping genuinely differs
    // (keyline columns vs stacked blocks) so it's expressed per layout,
    // but the link data is never duplicated. `descriptionMobileOnly`
    // keeps a row label-only in the dense desktop column while still
    // showing its description in the mobile drawer.
    // ─────────────────────────────────────────────────────────────

    // ── Product ──────────────────────────────────────────────────
    const productsSection: NavLinkSection = {
      title: t`Explore all products`,
      keyline: true,
      links: [
        {
          tier: "secondary",
          href: "/helpdesk",
          analyticsId: "link_nav_explore_all_products_helpdesk",
          title: t`Intercom helpdesk`,
          analyticsText: "Intercom helpdesk",
        },
        {
          tier: "secondary",
          href: "https://fin.ai",
          analyticsId: "link_nav_explore_all_products_fin_ai",
          title: t`Fin AI Agent`,
          analyticsText: "Fin AI Agent",
          external: true,
        },
      ],
    };
    const platformSection: NavLinkSection = {
      title: t`Platform`,
      keyline: true,
      links: [
        {
          tier: "secondary",
          href: "/helpdesk/omnichannel",
          analyticsId: "link_nav_platform_omnichannel",
          title: t`Channels`,
          analyticsText: "Channels",
        },
        {
          tier: "secondary",
          href: "/integrations",
          analyticsId: "link_nav_platform_integrations",
          title: t`Integrations`,
          analyticsText: "Integrations",
        },
        {
          tier: "secondary",
          href: "/security",
          analyticsId: "link_nav_platform_security",
          title: t`Safety & security`,
          analyticsText: "Safety & security",
        },
      ],
    };
    const capabilitiesSection: NavLinkSection = {
      title: t`Intercom capabilities`,
      keyline: true,
      links: [
        {
          tier: "secondary",
          href: "/helpdesk/inbox",
          analyticsId: "link_nav_intercom_capabilities_inbox",
          title: t`Inbox`,
          analyticsText: "Inbox",
        },
        {
          tier: "secondary",
          href: "/helpdesk/copilot",
          analyticsId: "link_nav_intercom_capabilities_copilot",
          title: t`Copilot`,
          analyticsText: "Copilot",
        },
        {
          tier: "secondary",
          href: "/helpdesk/tickets",
          analyticsId: "link_nav_intercom_capabilities_tickets",
          title: t`Tickets`,
          analyticsText: "Tickets",
        },
        {
          tier: "secondary",
          href: "/helpdesk/help-center",
          analyticsId: "link_nav_intercom_capabilities_help_center",
          title: t`Help Center`,
          analyticsText: "Help Center (product)",
        },
        {
          tier: "secondary",
          href: "/helpdesk/reporting",
          analyticsId: "link_nav_intercom_capabilities_reporting",
          title: t`Reporting`,
          analyticsText: "Reporting",
        },
        {
          tier: "secondary",
          href: "/helpdesk/outbound",
          analyticsId: "link_nav_intercom_capabilities_outbound",
          title: t`Outbound`,
          analyticsText: "Outbound",
        },
        {
          tier: "secondary",
          href: "/helpdesk/knowledge-hub",
          analyticsId: "link_nav_intercom_capabilities_knowledge_hub",
          title: t`Knowledge Hub`,
          analyticsText: "Knowledge Hub",
        },
      ],
    };

    // ── Customers ────────────────────────────────────────────────
    const customersLinks: NavLinkSection["links"] = [
      {
        tier: "primary",
        href: "/customers",
        analyticsId: "link_nav_customers",
        title: t`Meet our customers`,
        description: t`The world's most AI-forward companies run on Intercom`,
        analyticsText: "Meet our customers",
      },
      {
        prose: [
          t`Intercom for industries: `,
          {
            label: t`Technology`,
            href: "/solutions/technology",
            analyticsId: "link_nav_customers_prose_technology",
            analyticsText: "Technology",
          },
          t`, `,
          {
            label: t`Ecommerce`,
            href: "/solutions/ecommerce",
            analyticsId: "link_nav_customers_prose_ecommerce",
            analyticsText: "Ecommerce",
          },
          t`, `,
          {
            label: t`Fintech`,
            href: "/solutions/fintech",
            analyticsId: "link_nav_customers_prose_fintech",
            analyticsText: "Fintech",
          },
          t`, `,
          {
            label: t`Gaming`,
            href: "/solutions/gaming",
            analyticsId: "link_nav_customers_prose_gaming",
            analyticsText: "Gaming",
          },
        ],
      },
      {
        prose: [
          t`Intercom for scale: `,
          {
            label: t`Startups`,
            href: "/early-stage",
            analyticsId: "link_nav_customers_prose_startups",
            analyticsText: "Startups",
          },
          t`, `,
          {
            label: t`Small business`,
            href: "/small-business",
            analyticsId: "link_nav_customers_prose_small_business",
            analyticsText: "Small business",
          },
          t`, `,
          {
            label: t`Enterprise`,
            href: "/enterprise",
            analyticsId: "link_nav_customers_prose_enterprise",
            analyticsText: "Enterprise",
          },
        ],
      },
    ];

    // ── Resources ────────────────────────────────────────────────
    const learnSection: NavLinkSection = {
      title: t`Learn`,
      keyline: true,
      links: [
        {
          tier: "secondary",
          href: "/why-choose-intercom",
          analyticsId: "link_nav_learn_why_choose_intercom",
          title: t`Why choose Intercom?`,
          analyticsText: "Why choose Intercom?",
        },
        {
          tier: "secondary",
          href: "/early-stage",
          analyticsId: "link_nav_learn_early_stage",
          title: t`Early Stage`,
          analyticsText: "Early Stage",
        },
        {
          tier: "secondary",
          href: "https://www.intercom.com/blog",
          analyticsId: "link_nav_learn_blog",
          title: t`Intercom blog`,
          analyticsText: "Intercom blog",
          external: true,
        },
        {
          tier: "secondary",
          href: "https://events.intercom.com/",
          analyticsId: "link_nav_learn_events_intercom",
          title: t`Webinars & events`,
          analyticsText: "Webinars & events",
          external: true,
        },
        {
          tier: "secondary",
          href: "https://academy.fin.ai/",
          analyticsId: "link_nav_learn_academy_fin_ai",
          title: t`Academy`,
          analyticsText: "Academy",
          external: true,
        },
        {
          tier: "secondary",
          href: "/customer-transformation-report",
          analyticsId: "link_nav_learn_customer_transformation_report",
          title: t`2026 Transformation Report`,
          analyticsText: "2026 Transformation Report",
        },
        {
          tier: "secondary",
          href: "https://fin.ai/blueprint",
          analyticsId: "link_nav_learn_blueprint",
          title: t`AI Agent Blueprint`,
          analyticsText: "AI Agent Blueprint",
          external: true,
        },
      ],
    };
    const supportSection: NavLinkSection = {
      title: t`Support`,
      keyline: true,
      links: [
        {
          tier: "secondary",
          href: "https://www.intercom.com/help/en/",
          analyticsId: "link_nav_support_help_en",
          title: t`Help center`,
          analyticsText: "Help Center (resources)",
          external: true,
        },
        {
          tier: "secondary",
          href: "https://www.intercom.com/changes/en",
          analyticsId: "link_nav_support_changes_en",
          title: t`Product updates`,
          analyticsText: "Product updates",
          external: true,
        },
        {
          tier: "secondary",
          href: "https://community.intercom.com/",
          analyticsId: "link_nav_support_community_intercom",
          title: t`Community`,
          analyticsText: "Community",
          external: true,
        },
      ],
    };
    const companySection: NavLinkSection = {
      title: t`Company`,
      keyline: true,
      links: [
        {
          tier: "secondary",
          href: "https://fin.ai/about",
          analyticsId: "link_nav_company_about",
          title: t`About`,
          analyticsText: "About",
          external: true,
        },
        {
          tier: "secondary",
          href: "https://fin.ai/careers",
          analyticsId: "link_nav_company_careers",
          title: t`Careers`,
          analyticsText: "Careers",
          external: true,
        },
      ],
    };

    const intercomFixtureData: HeaderNavigation = {
      logo: {
        icon: (
          <>
            <IntercomMark className="block size-10 sm:hidden" />
            <IntercomLockup className="hidden h-5 w-auto -translate-y-[4px] sm:block" />
          </>
        ),
        href: "/",
        label: t`Intercom`,
      },
      primary: [
        {
          type: "dropdown",
          label: t`Product`,
          links: [[productsSection, platformSection], [capabilitiesSection]],
          mobileLinks: [productsSection, capabilitiesSection, platformSection],
        },
        {
          type: "dropdown",
          label: t`Customers`,
          links: [[{ spacious: true, links: customersLinks }]],
          mobileLinks: [{ links: customersLinks }],
        },
        {
          type: "dropdown",
          label: t`Resources`,
          links: [[learnSection], [supportSection, companySection]],
          mobileLinks: [learnSection, supportSection, companySection],
        },
        {
          type: "link",
          label: t`Pricing`,
          analyticsText: "Pricing",
          href: "/pricing",
          analyticsId: "link_nav_pricing",
        },
      ],
      secondary: [
        {
          type: "link",
          label: t`Log in`,
          analyticsText: "Log in",
          href: "https://app.intercom.com/admins/sign_in",
          analyticsId: "link_nav_log_in",
        },
        {
          type: "link",
          label: t`Contact sales`,
          analyticsText: "Contact sales",
          href: "/contact-sales",
          analyticsId: "link_nav_contact_sales",
        },
        {
          type: "link",
          label: t`View demo`,
          analyticsText: "View demo",
          href: "/view-demos",
          analyticsId: "link_nav_view_demos",
        },
        {
          type: "button",
          label: t`Start free trial`,
          analyticsText: "Start free trial",
          href: EXTERNAL_ROUTES.REACT_GENERIC_START_FREE_TRIAL,
          analyticsId: "btn_nav_start_free_trial",
        },
      ],
      crossSiteLink: {
        type: "button",
        label: t`Fin AI Agent`,
        analyticsText: "Fin AI Agent",
        variant: "secondary",
        isExternal: true,
        href: "https://fin.ai",
        analyticsId: "btn_nav_cross_site_fin_ai",
        tooltip: t`Our AI Agent and AI architecture trained specifically for customer service.`,
      },
      crossSiteCard: {
        description: t`Our AI Agent and AI architecture trained specifically for customer service`,
        accentColor: "fin",
      },
      banner: {
        text: t`Introducing Fin 4. A major step toward a fully agentic customer experience.`,
        textMobile: t`Introducing Fin 4.`,
        analyticsText: "Fin 4 launch banner",
        href: "https://fin.ai/fin4",
        linkLabel: t`Learn more`,
        target: "_self",
        disableMarquee: true,
        theme: "light",
        backgroundImages: {
          left: "https://cdn.sanity.io/images/4bgok82s/monorepo-static-assets/35972df63569796d2b7ec78b527687df0f5bdc32-1000x68.webp",
          right:
            "https://cdn.sanity.io/images/4bgok82s/monorepo-static-assets/2aaf11e61948c37e146e1a2bb4642177acb46042-1200x68.webp",
          mobile:
            "https://cdn.sanity.io/images/4bgok82s/monorepo-static-assets/39fa22b0eef6c2f9c1cb0bce0f9725e4fe7174d9-1170x102.webp",
        },
        disableCloseOnLinkClick: true,
      },
    };

    const intercomMobileMenuData: MobileMenuNavigation = {
      primary: [
        ...intercomFixtureData.primary,
      ] as MobileMenuNavigation["primary"],
      crossSiteLink: {
        label: t`Fin AI Agent`,
        analyticsText: "Fin AI Agent",
        href: "https://fin.ai",
      },
      secondary: [
        {
          type: "smallLink",
          label: t`Log in`,
          analyticsText: "Log in",
          href: "https://app.intercom.com/admins/sign_in",
          analyticsId: "link_nav_mobile_log_in",
        },
        {
          type: "smallLink",
          label: t`Contact sales`,
          analyticsText: "Contact sales",
          href: "/contact-sales",
          analyticsId: "link_nav_mobile_contact_sales",
        },
      ],
      tertiary: [
        {
          type: "button",
          label: t`View demo`,
          analyticsText: "View demo",
          href: "/view-demos",
          analyticsId: "btn_nav_mobile_view_demos",
        },
        {
          type: "button",
          label: t`Start free trial`,
          analyticsText: "Start free trial",
          variant: "primary",
          href: EXTERNAL_ROUTES.REACT_GENERIC_START_FREE_TRIAL,
          analyticsId: "btn_nav_mobile_start_free_trial",
        },
      ],
    };

    // Page Specific Fixtures

    // Reduced header fixture for /view-demos
    const viewDemosFixtureData: HeaderNavigation = {
      logo: intercomFixtureData.logo,
      primary: [],
      secondary: [
        {
          type: "link",
          label: t`Contact sales`,
          analyticsText: "Contact sales",
          href: "/contact-sales",
          analyticsId: "link_nav_view_demos_contact_sales",
        },
        {
          type: "button",
          label: t`Start free trial`,
          analyticsText: "Start free trial",
          href: EXTERNAL_ROUTES.REACT_GENERIC_START_FREE_TRIAL,
          analyticsId: "btn_nav_view_demos_start_free_trial",
        },
      ],
      crossSiteLink: intercomFixtureData.crossSiteLink,
    };

    // Minimal header for /view-demos after gate submission with ≤15 employees
    const viewDemosSmallCompanyFixtureData: HeaderNavigation = {
      logo: intercomFixtureData.logo,
      primary: [],
      secondary: [
        {
          type: "button",
          label: t`Start free trial`,
          analyticsText: "Start free trial",
          href: EXTERNAL_ROUTES.REACT_GENERIC_START_FREE_TRIAL,
          analyticsId: "btn_nav_view_demos_small_company_start_free_trial",
        },
      ],
      crossSiteLink: intercomFixtureData.crossSiteLink,
    };

    // Minimal header for /view-demos after gate submission with ≤15 employees
    const viewDemosZendeskFlowFixtureData: HeaderNavigation = {
      logo: intercomFixtureData.logo,
      primary: [],
      secondary: [
        {
          type: "button",
          label: t`Contact sales`,
          analyticsText: "Contact sales",
          href: "/contact-sales",
          analyticsId: "btn_nav_view_demos_zendesk_contact_sales",
        },
      ],
      crossSiteLink: intercomFixtureData.crossSiteLink,
    };

    return {
      intercomFixtureData,
      intercomMobileMenuData,
      viewDemosFixtureData,
      viewDemosSmallCompanyFixtureData,
      viewDemosZendeskFlowFixtureData,
    };
  }, [t]);
}
